import { useEffect, useState } from 'react';
import { getOnboardingQueue, updateAdminChecklist, GetOnboardingQueueOutputType } from 'zite-endpoints-sdk';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { CheckSquare, Square } from 'lucide-react';

type Submission = GetOnboardingQueueOutputType['submissions'][0];

const CHECKLIST_FIELDS: { key: keyof NonNullable<Submission['checklist']>; label: string }[] = [
  { key: 'rtwChecked', label: 'RTW Checked' },
  { key: 'payrollAdded', label: 'Payroll Added' },
  { key: 'contractIssued', label: 'Contract Issued' },
  { key: 'pensionEnrolmentCompleted', label: 'Pension Set Up' },
  { key: 'handbookIssued', label: 'Handbook Issued' },
  { key: 'itSystemsAccess', label: 'IT/Systems Access' },
];

const fmtDate = (d?: string) => { try { return d ? format(parseISO(d), 'dd MMM yyyy, h:mm a') : '—'; } catch { return '—'; } };

function SubmissionCard({ sub, onUpdate }: { sub: Submission; onUpdate: (userId: string, field: string, value: boolean) => Promise<void> }) {
  const checklist = sub.checklist || { rtwChecked: false, payrollAdded: false, contractIssued: false, pensionEnrolmentCompleted: false, handbookIssued: false, itSystemsAccess: false };
  const completedItems = Object.values(checklist).filter(Boolean).length - (sub.checklist?.checklistId ? 0 : 0);
  const totalItems = CHECKLIST_FIELDS.length;

  const statusColors: Record<string, string> = {
    'Submitted': 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    'In Progress': 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    'Approved': 'bg-green-500/10 text-green-500 border-green-500/20',
  };

  return (
    <div className="border border-border rounded-lg p-4 bg-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <p className="font-medium text-foreground">{sub.userName}</p>
          <p className="text-xs text-muted-foreground">{sub.email} · Form submitted {fmtDate(sub.submittedAt)}</p>
        </div>
        <span className={`text-xs px-2 py-0.5 border rounded-sm shrink-0 ${statusColors[sub.onboardingStatus] || 'text-muted-foreground bg-muted border-border'}`}>
          {sub.onboardingStatus}
        </span>
      </div>

      <div className="flex items-center gap-3 mb-3 text-xs text-muted-foreground">
        <span>Steps: <strong className="text-foreground">{sub.stepsCompleted}/7</strong></span>
        <span>HR checklist: <strong className="text-foreground">{completedItems}/{totalItems}</strong></span>
        {sub.isLocked && <span className="text-green-500 font-medium">🔒 Form locked</span>}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {CHECKLIST_FIELDS.map(({ key, label }) => {
          const checked = (checklist as any)[key] || false;
          return (
            <button key={String(key)} onClick={() => onUpdate(sub.userId, String(key), !checked)}
              className={`flex items-center gap-2 text-xs p-2 border rounded transition-colors ${checked ? 'border-green-500/30 bg-green-500/5 text-green-500' : 'border-border text-muted-foreground hover:border-primary/40'}`}>
              {checked ? <CheckSquare className="w-3.5 h-3.5 shrink-0" /> : <Square className="w-3.5 h-3.5 shrink-0" />}
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function OnboardingQueueTab() {
  const [data, setData] = useState<GetOnboardingQueueOutputType | null>(null);
  const [loading, setLoading] = useState(true);

  const load = () => getOnboardingQueue({}).then(setData).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const handleUpdate = async (userId: string, field: string, value: boolean) => {
    try {
      await updateAdminChecklist({ employeeId: userId, [field]: value } as any);
      setData(d => d ? { ...d, submissions: d.submissions.map(s => s.userId === userId ? { ...s, checklist: { ...(s.checklist || { rtwChecked: false, payrollAdded: false, contractIssued: false, pensionEnrolmentCompleted: false, handbookIssued: false, itSystemsAccess: false }), [field]: value } } : s) } : d);
    } catch { toast.error('Failed to update checklist'); }
  };

  if (loading) return <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-40" />)}</div>;

  const subs = data?.submissions || [];

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div><h2 className="text-sm font-semibold text-foreground">Onboarding Queue</h2><p className="text-xs text-muted-foreground mt-0.5">{subs.filter(s => s.onboardingStatus === 'Submitted').length} awaiting review · {subs.length} total active</p></div>
      </div>
      {subs.length === 0
        ? <div className="text-center py-12 text-muted-foreground text-sm">No onboarding submissions yet</div>
        : <div className="space-y-3">{subs.map(s => <SubmissionCard key={s.userId} sub={s} onUpdate={handleUpdate} />)}</div>}
    </div>
  );
}
